import { axiosCall } from "@/utils/axiosCall";
import { cloneDeep } from "lodash";

function initialState() {
  return {
    selectedEvaluation: null,
    selectedRequirement: null,
    requirementList: null,
    requirementIndex: null,
    isOrganization: null,
    savingStatus: "success",
    requirementsPaths: {},
    evidences: [],
    reportConventionLevel: null
  };
}

const state = initialState;

const getters = {
  getSelectedEvaluation: (state) => {
    let evaluation = cloneDeep(state.selectedEvaluation);
    if (evaluation) {
      setEvidencesScoreToEvaluations(evaluation.evaluation, state.evidences);
    }
    return evaluation;
  },
  getSelectedRequirement: (state) => state.selectedRequirement,
  getRequirementList: (state) => state.requirementList,
  getRequirementIndex: (state) => state.requirementIndex,
  getSavingStatus: (state) => state.savingStatus,
  getReportConventionLevel: (state) => state.reportConventionLevel,
};

const mutations = {
  setLastEvaluated: (state, data) => {
    state.lastEvaluated = data;
  },
  SET_SELECTED_EVALUATION: (state, evaluation) => {
    state.selectedEvaluation = evaluation;
  },
  SET_SELECTED_REQUIREMENT: (state, requirement) => {
    state.selectedRequirement = requirement;
    state.requirementIndex = state.requirementList.findIndex(
      (req) => requirement.id === req.id
    );
  },
  CLEAR_SELECTED_REQUIREMENT: (state) => {
    state.selectedRequirement = null;
    state.requirementIndex = null;
  },
  SET_REQUIREMENT_LIST: (state, requirementList) => {
    state.requirementList = requirementList;
  },
  SET_IS_ORGANIZATION: (state, isOrganization) => {
    state.isOrganization = isOrganization;
  },
  SET_SAVING_STATUS: (state, savingStatus) => {
    state.savingStatus = savingStatus;
  },
  SET_REQUIREMENTS_PATHS: (state, requirementsPaths) => {
    state.requirementsPaths = requirementsPaths;
  },
  SET_NODES_TO_IMPROVE: (state, nodesIdToImprove) => {
    state.nodesIdToImprove = nodesIdToImprove;
  },
  SET_EVALUATION_EVIDENCES: (state, evidences) => {
    state.evidences = evidences;
  },
  CLEAR_EVALUATION_STORE: (state) => {
    let init = { ...initialState() };

    Object.keys(state).forEach((key) => {
      state[key] = init[key];
    });
  },
  SET_REPORT_CONVENTION_LEVEL: (state, reportConventionLevel) => {
    state.reportConventionLevel = reportConventionLevel;
  },
};

const actions = {
  async getEvaluation(
    { commit, state, dispatch },
    { requirementId, evaluationId } = {}
  ) {
    let response = await axiosCall({
      url: `Evaluations/${evaluationId || state.selectedEvaluation.id}`,
      method: "GET",
    });
    let evaluation = response.data;
    let nodesToImprove = extractNodesToImprove(evaluation);
    setImprovementsScore(evaluation.evaluation, nodesToImprove);
    commit("SET_SELECTED_EVALUATION", evaluation);

    let { requirementList, pathList } = getRequirementsAsList(
      evaluation.evaluation,
      null,
      [],
      state.isOrganization
    );
    commit("SET_REQUIREMENT_LIST", requirementList);
    commit("SET_REQUIREMENTS_PATHS", pathList);
    if (requirementId) {
      let requirement = findRequirementById(
        evaluation.evaluation,
        requirementId
      );
      commit("SET_SELECTED_REQUIREMENT", requirement);
    }

    //get evaluation evidences
    dispatch("getEvaluationEvidences");
  },
  async getEvaluationEvidences({ commit, state }, evaluationId) {
    let response = await axiosCall({
      url: `Evidences/getEvidencesForEvaluation/${
        evaluationId || state.selectedEvaluation.id
      }`,
      method: "GET",
    });
    let evidences = response.data;
    commit("SET_EVALUATION_EVIDENCES", evidences);
  },
  async updateEvaluation(
    { state, rootState, commit },
    { valuesToUpdate, valuesToPush, calculateProgress, checkReadyToSend }
  ) {
    commit("SET_SAVING_STATUS", "pending");
    let newObjectWithFullPath = {};
    let newPushObjectWithFullPath = {};
    let fullpath = "evaluation";

    newObjectWithFullPath = {
      ...newObjectWithFullPath,
      ...getLastUpdated(fullpath, rootState),
    };

    if (state.selectedRequirement && state.selectedRequirement.id) {
      let selectedRequirementPath =
        state.requirementsPaths[state.selectedRequirement.id];
      selectedRequirementPath.forEach((index) => {
        fullpath += `.children.${index}`;
        newObjectWithFullPath = {
          ...newObjectWithFullPath,
          ...getLastUpdated(fullpath, rootState),
        };
      });
    }
    Object.keys(valuesToUpdate || {}).forEach((key) => {
      newObjectWithFullPath[`${fullpath}.${key}`] = valuesToUpdate[key];
    });

    Object.keys(valuesToPush || {}).forEach((key) => {
      newPushObjectWithFullPath[`${fullpath}.${key}`] = valuesToPush[key];
    });
    if (calculateProgress) {
      newObjectWithFullPath = {
        ...newObjectWithFullPath,
        ...makeAllCalculations(
          cloneDeep(state.selectedEvaluation.evaluation),
          [],
          state,
          newObjectWithFullPath
        ),
      };
    }
    if (checkReadyToSend) {
      newObjectWithFullPath[`readyToSendEvidences`] = checkTreeIsReadyToSend(
        cloneDeep(state.selectedEvaluation.evaluation),
        [],
        newObjectWithFullPath
      );
      newObjectWithFullPath["evaluation.evidencesSubmitted"] = false;
    }
    let resp = await axiosCall({
      url: `/Evaluations/partial/${state.selectedEvaluation.id}`,
      method: "PATCH",
      data: { set: newObjectWithFullPath, push: newPushObjectWithFullPath },
    }).catch(() => commit("SET_SAVING_STATUS", "error"));
    let evaluation = resp.data;
    let nodesToImprove = extractNodesToImprove(evaluation);

    setImprovementsScore(evaluation.evaluation, nodesToImprove);
    commit("SET_SELECTED_EVALUATION", evaluation);
    let { requirementList, pathList } = getRequirementsAsList(
      evaluation.evaluation,
      null,
      [],
      state.isOrganization
    );
    commit("SET_REQUIREMENT_LIST", requirementList);
    commit("SET_REQUIREMENTS_PATHS", pathList);
    if (state.selectedRequirement && state.selectedRequirement.id) {
      let requirement = findRequirementById(
        evaluation.evaluation,
        state.selectedRequirement.id
      );
      commit("SET_SELECTED_REQUIREMENT", requirement);
    }
    commit("SET_SAVING_STATUS", "success");
  },
};

function findRequirementById(requirement, id) {
  if (requirement.children && requirement.children.length) {
    for (let i = 0; i < requirement.children.length; i++) {
      let foundRequirement = findRequirementById(requirement.children[i], id);
      if (foundRequirement) {
        return foundRequirement;
      }
    }
  }
  if (requirement.id === id) {
    return requirement;
  }

  return null;
}

function getRequirementsAsList(node, parentNode, path, isOrganization = true) {
  let tempNode = null;
  let requirementList = [];
  let pathList = {};
  if (node.children && node.children.length > 0) {
    if (
      node.evaluation_administrator_node_nomenclature &&
      node.evaluation_administrator_node_requirementName
    ) {
      tempNode = {
        nomenclature: node.evaluation_administrator_node_nomenclature,
        name: node.evaluation_administrator_node_requirementName,
        parent: parentNode,
      };
    }
    node.children.map((child, index) => {
      let { requirementList: rl, pathList: pl } = getRequirementsAsList(
        child,
        tempNode,
        [...path, index],
        isOrganization
      );
      requirementList = [...requirementList, ...rl];
      pathList = { ...pathList, ...pl };
    });
  } else {
    node.parent = parentNode;
    if (!isOrganization || needsEvidences(node)) {
      requirementList.push(node);
    }
    pathList[node.id] = path;
  }
  return { requirementList, pathList };
}

function needsEvidences(node) {
  if (
    node.evaluation_administrator_node_editRequirement_requireEvidences ===
      true ||
    node.evaluation_administrator_node_editRequirement_evidencesReady
  ) {
    return true;
  } else {
    return false;
  }
}

export default {
  state,
  getters,
  actions,
  mutations,
};

function getLastUpdated(path, rootState) {
  return {
    [`${path}.evaluation_last_updated`]: new Date().toLocaleDateString("en-US"),
    [`${path}.userAccount`]: {
      account: rootState.userAccount.userAccount,
    },
  };
}

function makeAllCalculations(
  evaluation,
  path,
  state,
  otherFieldsThatWillBeUpdated
) {
  let fieldsToUpdate = {};

  const progress = getTotalLeafEvaluated(evaluation);
  fieldsToUpdate[`evaluation.evaluationLeafProgress`] = {
    totalEvaluated: progress.totalEvaluated,
    totalChildren: progress.totalChildren,
  };
  fieldsToUpdate[`hasIndicators`] = progress.hasIndicators;
  fieldsToUpdate[`achievedSeal`] = getAchievedSeal(
    evaluation.evaluation_status,
    evaluation.readyGold,
    evaluation.readySilver,
    evaluation.readyBronze,
    state
  );

  fieldsToUpdate = {
    ...fieldsToUpdate,
    ...fillProgress(evaluation, path, {
      ...fieldsToUpdate,
      ...otherFieldsThatWillBeUpdated,
    }),
  };

  return fieldsToUpdate;
}

function getAchievedSeal(score, readyGold, readySilver, readyBronze, state) {
  if (
    readyGold &&
    score >= parseFloat(state.selectedEvaluation.evaluation_gold)
  ) {
    return "label_evaluation_gold";
  }

  if (
    readySilver &&
    score >= parseFloat(state.selectedEvaluation.evaluation_silver)
  ) {
    return "label_evaluation_silver";
  }

  if (
    readyBronze &&
    score >= parseFloat(state.selectedEvaluation.evaluation_bronze)
  ) {
    return "label_evaluation_bronze";
  }
  return "label_evaluation_sealNotAchieved";
}

function checkTreeIsReadyToSend(node, path, otherUpdatingFields) {
  let ready = true;
  if (node.children && node.children.length > 0) {
    node.children.map((child, childrenIndex) => {
      ready =
        ready &&
        checkTreeIsReadyToSend(
          child,
          [...path, childrenIndex],
          otherUpdatingFields
        );
    });
  } else {
    let pathString = "";
    path.forEach((index) => {
      pathString += `.children.${index}`;
    });

    let evidenceStatusValue = "";
    if (
      typeof otherUpdatingFields[
        `evaluation${pathString}.evaluation_administrator_node_editRequirement_evidencesReady`
      ] !== "undefined"
    ) {
      evidenceStatusValue =
        otherUpdatingFields[
          `evaluation${pathString}.evaluation_administrator_node_editRequirement_evidencesReady`
        ];
    } else {
      evidenceStatusValue =
        node.evaluation_administrator_node_editRequirement_evidencesReady;
    }
    if (
      node.evaluation_administrator_node_editRequirement_requireEvidences ===
      true
    ) {
      ready =
        ready &&
        [
          "label_evaluation_evidencesRequested_status_ready",
          "label_evaluation_evidencesRequested_status_submitted",
          "label_evaluation_evidencesRequested_status_resubmitted",
        ].includes(evidenceStatusValue);
    }
  }
  return ready;
}

function getTotalLeafEvaluated(node) {
  let totalEvaluated = 0;
  let totalChildren = 0;
  let hasIndicators = false;
  if (node.children && node.children.length > 0) {
    node.children.map((child) => {
      if (child.children && child.children.length > 0) {
        const result = getTotalLeafEvaluated(child);
        totalEvaluated = totalEvaluated + result.totalEvaluated;
        totalChildren = totalChildren + result.totalChildren;
        hasIndicators = hasIndicators || result.hasIndicators;
      } else {
        totalChildren++;
        if (!isNaN(child.evaluation_status)) {
          totalEvaluated++;
        }
        if (child.evaluation_administrator_node_matrix_indicator_indicator) {
          hasIndicators = true;
        }
      }
    });
  }
  return { totalEvaluated, totalChildren, hasIndicators };
}

function fillProgress(node, path, otherUpdatingFields) {
  let fieldsToUpdate = {};
  let pathString = "";
  path.forEach((index) => {
    pathString += `.children.${index}`;
  });
  if (node.children && node.children.length > 0) {
    let total = 0;
    let readyGold = true;
    let readySilver = true;
    let readyBronze = true;
    node.children.map((child, index) => {
      fieldsToUpdate = {
        ...fieldsToUpdate,
        ...fillProgress(child, [...path, index], otherUpdatingFields),
      };
    });
    node.children.map((child, childrenIndex) => {
      if (!isNaN(child.evaluation_status)) {
        if (child.children && child.children.length > 0) {
          total = total + parseFloat(child.evaluation_status);
        } else {
          let evaluationStatusValue = 0;
          if (
            typeof otherUpdatingFields[
              `evaluation.children.${childrenIndex}${pathString}.evaluation_status`
            ] !== "undefined"
          ) {
            evaluationStatusValue =
              otherUpdatingFields[
                `evaluation.children.${childrenIndex}${pathString}.evaluation_status`
              ];
          } else {
            evaluationStatusValue = child.evaluation_status;
          }
          total =
            total +
            (evaluationStatusValue *
              child.evaluation_administrator_node_percentualWeight) /
              100;
        }
      }
      readyGold = readyGold && child.readyGold;
      readySilver = readySilver && child.readySilver;
      readyBronze = readyBronze && child.readyBronze;
      if (total >= node.evaluation_administrator_node_percentualWeight) {
        // eslint-disable-next-line camelcase
        node.evaluation_status =
          node.evaluation_administrator_node_percentualWeight;
        fieldsToUpdate[`evaluation${pathString}.evaluation_status`] =
          node.evaluation_administrator_node_percentualWeight;
      } else {
        // eslint-disable-next-line camelcase
        node.evaluation_status = total.toFixed(2);
        fieldsToUpdate[`evaluation${pathString}.evaluation_status`] =
          total.toFixed(2);
      }
      node.readyGold = readyGold;
      fieldsToUpdate[`evaluation${pathString}.readyGold`] = readyGold;
      node.readySilver = readySilver;
      fieldsToUpdate[`evaluation${pathString}.readySilver`] = readySilver;
      node.readyBronze = readyBronze;
      fieldsToUpdate[`evaluation${pathString}.readyBronze`] = readyBronze;
      // eslint-disable-next-line camelcase
      if (node.name === "root" && node.evaluation_status > 100) {
        node.evaluation_status = 100;
        fieldsToUpdate[`evaluation${pathString}.evaluation_status`] = 100;
      }
    });
    var totalP = 0;
    node.children.map((s) => {
      if (s.children && s.children.length > 0) {
        if (s.evaluation_progress === s.children.length) {
          totalP++;
        }
      } else {
        if (!isNaN(s.evaluation_status)) {
          totalP++;
        }
      }
    });
    // eslint-disable-next-line camelcase
    node.evaluation_progress = totalP;
    fieldsToUpdate[`evaluation${pathString}.evaluation_progress`] = totalP;
  } else {
    node.readyGold =
      typeof node.evaluation_gold !== "undefined" &&
      node.evaluation_gold === true
        ? parseFloat(node.evaluation_status) > 0
        : true;
    fieldsToUpdate[`evaluation${pathString}.readyGold`] =
      typeof node.evaluation_gold !== "undefined" &&
      node.evaluation_gold === true
        ? parseFloat(node.evaluation_status) > 0
        : true;
    node.readySilver =
      typeof node.evaluation_silver !== "undefined" &&
      node.evaluation_silver === true
        ? parseFloat(node.evaluation_status) > 0
        : true;
    fieldsToUpdate[`evaluation${pathString}.readySilver`] =
      typeof node.evaluation_silver !== "undefined" &&
      node.evaluation_silver === true
        ? parseFloat(node.evaluation_status) > 0
        : true;
    node.readyBronze =
      typeof node.evaluation_bronze !== "undefined" &&
      node.evaluation_bronze === true
        ? parseFloat(node.evaluation_status) > 0
        : true;
    fieldsToUpdate[`evaluation${pathString}.readyBronze`] =
      typeof node.evaluation_bronze !== "undefined" &&
      node.evaluation_bronze === true
        ? parseFloat(node.evaluation_status) > 0
        : true;
  }
  return fieldsToUpdate;
}

function extractNodesToImprove(evaluation) {
  let nodes = [];
  if (
    evaluation &&
    evaluation.evaluation &&
    evaluation.evaluation.improvements &&
    evaluation.evaluation.improvements.length
  ) {
    evaluation.evaluation.improvements.forEach((improvement) => {
      improvement.nodesToImprove.forEach((nodeToImprove) => {
        nodes.push(nodeToImprove.id);
      });
    });
  }
  return nodes;
}

function setEvidencesScoreToEvaluations(requirement, evidences) {
  if (requirement.children && requirement.children.length) {
    requirement.totalEvidences = 0;
    requirement.evidencesStatusScore = 10;

    let worstChildScore = -10;
    for (let child of requirement.children) {
      setEvidencesScoreToEvaluations(child, evidences);
      if (child.evidencesStatusScore < requirement.evidencesStatusScore) {
        requirement.evidencesStatusScore = child.evidencesStatusScore;
      }
      if (child.totalEvidences) {
        requirement.totalEvidences += child.totalEvidences;
      }
      if (child.evidencesScore && worstChildScore < child.evidencesScore) {
        worstChildScore = child.evidencesScore;
      }
    }
    if (worstChildScore !== -10) {
      requirement.evidencesScore = worstChildScore;
    }
  } else {
    if (
      requirement.evaluation_administrator_node_editRequirement_requireEvidences
    ) {
      requirement.evidencesStatusScore = getEvidenceStatusScore(
        requirement.evaluation_administrator_node_editRequirement_evidencesReady
      );

      let ownedEvidencesForRequirement = evidences.filter(({ requirements }) =>
        requirements.some(
          ({ requirementId, typeOfAssociation }) =>
            requirementId === requirement.id.toString() &&
            typeOfAssociation === "owner"
        )
      );
      let linkedEvidencesForRequirement = evidences.filter(({ requirements }) =>
        requirements.some(
          ({ requirementId, typeOfAssociation }) =>
            requirementId === requirement.id.toString() &&
            typeOfAssociation === "link"
        )
      );
      requirement.evidencesScore = 0;
      requirement.totalEvidences = 0;
      if (
        requirement.evidences &&
        requirement.evidences.comments &&
        requirement.evidences.comments.length
      ) {
        requirement.evidencesScore = 1;

        requirement.totalEvidences += requirement.evidences.comments.length;
      }

      if (linkedEvidencesForRequirement.length > 0) {
        requirement.evidencesScore = 2;
        requirement.totalEvidences += linkedEvidencesForRequirement.length;
      }

      if (ownedEvidencesForRequirement.length > 0) {
        requirement.evidencesScore = 3;
        requirement.totalEvidences += ownedEvidencesForRequirement.length;
      }
    }
  }
}

function getEvidenceStatusScore(status) {
  switch (status) {
    case "label_evaluation_evidencesRequested_status_submitted":
      return 5;
    case "label_evaluation_evidencesRequested_status_ready":
      return 4;
    case "label_evaluation_evidencesRequested_status_pending":
      return 3;
    case "label_evaluation_evidencesRequested_status_inProgress":
      return 2;
    case "label_evaluation_evidencesRequested_status_resubmit":
      return 1;
    default:
      return 0;
  }
}

function setImprovementsScore(model, nodesIdToImprove) {
  let childScore = 0;
  if (model.children && model.children.length) {
    let hasOpportunityOfImprovementAsGeneralComment = false;
    model.children.forEach((child) => {
      let hasImprovementInComments = false;
      if (child.comments?.length) {
        hasImprovementInComments =
          child.comments.filter(({ opportunity }) => opportunity).length > 0;
      }
      setImprovementsScore(child, nodesIdToImprove);
      hasOpportunityOfImprovementAsGeneralComment =
        hasOpportunityOfImprovementAsGeneralComment ||
        child.hasOpportunityOfImprovementAsGeneralComment ||
        hasImprovementInComments;

      if (childScore < child.improvementScore) {
        childScore = child.improvementScore;
      }
    });
    model.hasOpportunityOfImprovementAsGeneralComment =
      hasOpportunityOfImprovementAsGeneralComment;
  }

  if (model.comments?.length) {
    model.hasOpportunityOfImprovementAsGeneralComment =
      model.comments.filter(({ opportunity }) => opportunity).length > 0;
  }

  if (nodesIdToImprove && nodesIdToImprove.includes(model.id)) {
    model.hasImprovement = true;
  }

  if (model.hasImprovement) {
    model.improvementScore = 2;
  } else if (model.hasOpportunityOfImprovementAsGeneralComment) {
    model.improvementScore = 1;
  } else {
    model.improvementScore = 0;
  }

  if (model.improvementScore < childScore) {
    model.improvementScore = childScore;
  }
}
